import React, { useEffect, useState } from 'react';
import { 
    Globe, Smartphone, Monitor, FileText, Search, Rocket, 
    Layout, Network, GraduationCap, ArrowRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
    Globe: Globe,
    Smartphone: Smartphone,
    Monitor: Monitor,
    FileText: FileText,
    Search: Search,
    Rocket: Rocket,
    Layout: Layout,
    Network: Network,
    GraduationCap: GraduationCap
};

export const Services = () => {
    const { t, language } = useLanguage();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchServices = async () => {
            try {
                const response = await axios.get(`${API}/services/public`);
                setServices(response.data);
            } catch (error) {
                console.error('Error fetching services:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchServices();
    }, []);

    const getIcon = (iconName) => {
        const IconComponent = iconMap[iconName] || FileText;
        return <IconComponent className="w-6 h-6" />;
    };

    const getCategoryColor = (categorie) => {
        switch (categorie) {
            case 'Développement':
                return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            case 'Conseil':
                return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
            case 'Formation':
                return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
            default:
                return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
        }
    };

    const groupedServices = services.reduce((acc, service) => {
        const cat = service.categorie || 'Autre';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(service);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <main data-testid="services-page" className="min-h-screen pt-24">
            {/* Header */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
                            Services
                        </h1>
                        <p className="text-lg text-slate-400">
                            {t(
                                'DKR CONSULTING propose une gamme complète de services IT pour accompagner votre transformation digitale.',
                                'DKR CONSULTING offers a complete range of IT services to support your digital transformation.'
                            )}
                        </p>
                    </div>
                </div>
            </section>

            {/* Services by Category */}
            {Object.entries(groupedServices).map(([category, categoryServices]) => (
                <section key={category} className="pb-16" data-testid={`services-${category}`}>
                    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                        <h2 className="text-2xl md:text-3xl font-semibold text-white mb-8">
                            {category}
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {categoryServices.map((service, index) => (
                                <div 
                                    key={service.id}
                                    className="glass-card p-6 card-hover group fade-in"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                    data-testid={`service-${service.id}`}
                                >
                                    {/* Icon */}
                                    <div className={`p-3 rounded-xl ${getCategoryColor(service.categorie)} w-fit mb-4`}>
                                        {getIcon(service.icon)}
                                    </div>

                                    {/* Content */}
                                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                                        {language === 'en' && service.nom_en ? service.nom_en : service.nom}
                                    </h3>
                                    <p className="text-white font-medium text-sm mb-4">
                                        {language === 'en' && service.description_en ? service.description_en : service.description}
                                    </p>

                                    {/* Price range if available */}
                                    {(service.prix_min || service.prix_max) && (
                                        <div className="text-sm text-white font-medium">
                                            {service.prix_min && service.prix_max 
                                                ? `${service.prix_min}€ - ${service.prix_max}€`
                                                : service.prix_min 
                                                    ? t(`À partir de ${service.prix_min}€`, `From ${service.prix_min}€`)
                                                    : t(`Jusqu'à ${service.prix_max}€`, `Up to ${service.prix_max}€`)
                                            }
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ))}

            {/* CTA */}
            <section className="py-16 md:py-24" data-testid="services-cta">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                    <div className="glass-card p-12 text-center relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-violet-500/10"></div>
                        <div className="relative z-10">
                            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
                                {t('Besoin d\'un service sur mesure ?', 'Need a custom service?')}
                            </h2>
                            <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
                                {t(
                                    'Chaque projet est unique. Contactez-moi pour discuter de vos besoins spécifiques et obtenir un devis personnalisé.',
                                    'Every project is unique. Contact me to discuss your specific needs and get a personalized quote.'
                                )}
                            </p>
                            <Link to="/contact">
                                <Button 
                                    size="lg"
                                    className="btn-glow bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8"
                                    data-testid="services-contact-btn"
                                >
                                    {t('Demander un devis', 'Request a quote')}
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};
